/**
 * Session boot milestones — the shared vocabulary between the overlay
 * container (which owns the requests) and `SessionBootLoader` (which only
 * renders).
 *
 * PRD PE-8.2: "a staged loader that does not track reality is a lie the user
 * can feel". The loader this replaces (`SessionView.vue`) advanced a scripted
 * three-step script on a 3.5s interval regardless of what the backend was
 * doing, so it read as finished while the request was still in flight and as
 * hung once it ran out of steps.
 *
 * The wait being covered is real and very variable: PRD §8.1 measured 16.2s
 * TTFB on a scale-from-zero against `GET /health` (of which 14.7s is Python
 * import before the app's first log line) versus 0.104s warm, and a session
 * boot is that *plus* generation. So the vocabulary here is deliberately
 * event-shaped rather than time-shaped — a milestone only moves when a real
 * await resolves, and the copy for a milestone never claims a step that has
 * not started.
 */

import type { SessionMode } from "./focusSession";

export type BootMilestone =
  "idle" | "requested" | "session_created" | "generating" | "ready" | "failed";

/** Happy-path order. `failed` is off-path and can replace any of these. */
export const BOOT_MILESTONE_ORDER: readonly BootMilestone[] = [
  "idle",
  "requested",
  "session_created",
  "generating",
  "ready",
];

export interface BootCopy {
  /**
   * Stable for the whole boot. Only the status line moves, so the heading does
   * not flicker every time a milestone lands.
   */
  title: string;
  /** Replaces `title` on `failed`, where "warming up" is no longer true. */
  failedTitle: string;
  /** Sits under the bar. True of every boot, cold or warm. */
  reassurance: string;
  status: Record<BootMilestone, string>;
}

/**
 * Copy as data, one row per mode. Each status line describes the step that has
 * *already begun*, so it is true at the moment it is shown.
 *
 * Tutor mode reaches `ready` off a single request, so its middle milestones may
 * be skipped entirely; they still need honest wording for the runs that do pass
 * through them.
 */
export const BOOT_COPY: Record<SessionMode, BootCopy> = {
  question: {
    title: "Warming up your tutor",
    failedTitle: "Couldn’t start your session",
    reassurance:
      "The tutor starts up on demand, then writes a question against your board’s mark scheme.",
    status: {
      idle: "Getting ready…",
      requested: "Waking the tutor…",
      session_created: "Loading the mark scheme…",
      generating: "Writing a fresh question…",
      ready: "Ready.",
      failed:
        "The tutor didn’t answer. It may still be waking up — worth another go in a moment.",
    },
  },
  tutor: {
    title: "Warming up your tutor",
    failedTitle: "Couldn’t start your session",
    reassurance:
      "The tutor starts up on demand, then reads up on the topic you picked.",
    status: {
      idle: "Getting ready…",
      requested: "Waking the tutor…",
      session_created: "Reading up on your topic…",
      generating: "Working out where to start…",
      ready: "Ready.",
      failed:
        "The tutor didn’t answer. It may still be waking up — worth another go in a moment.",
    },
  },
  mock: {
    title: "Setting up exam conditions",
    failedTitle: "Couldn’t start your mock",
    reassurance:
      "The tutor starts up on demand, then writes a full set of questions before the clock starts.",
    status: {
      idle: "Getting ready…",
      requested: "Waking the tutor…",
      session_created: "Setting up exam conditions…",
      generating: "Writing your questions…",
      ready: "Ready.",
      failed:
        "The paper didn’t come back. The tutor may still be waking up — worth another go.",
    },
  },
};

/**
 * Ring fill, 0–1. Deliberately **not** time-based: it advances only when
 * reality does, which is the whole point of PE-8.2. `generating` stops at 0.8
 * rather than creeping towards 1, because the longest and least predictable
 * part of the wait sits inside it and a ring that arrives before the content
 * does is the same lie in a different shape.
 *
 * `failed` reports no progress at all — a failure is not partial success, and
 * a half-filled ring beside an error message invites the student to keep
 * waiting for something that is never coming.
 */
export function milestoneFraction(milestone: BootMilestone): number {
  switch (milestone) {
    case "requested":
      return 0.25;
    case "session_created":
      return 0.5;
    case "generating":
      return 0.8;
    case "ready":
      return 1;
    // idle: nothing has been asked for yet. failed: nothing is coming.
    default:
      return 0;
  }
}

/**
 * Elapsed wait, in the shape a person reads out loud ("18s", "1m 20s").
 * Distinct from `formatClock`, which is a countdown/stopwatch face: a `0:18`
 * here would read as a timer the student is being measured against.
 */
export function formatElapsed(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  if (safe < 60) return `${safe}s`;
  const mins = Math.floor(safe / 60);
  const rest = safe % 60;
  return rest === 0 ? `${mins}m` : `${mins}m ${rest}s`;
}

/**
 * When the container is still waking, honesty beats a spinner. Warm TTFB is
 * 0.104s, so anything still sitting on `requested` after 5s is a
 * scale-from-zero with near-certainty (§8.1) — saying so is both true and more
 * reassuring than motion with no explanation.
 */
export const WAKE_ESCALATION_SECONDS = 5;

/**
 * Generation is slow even on a warm container — the loader this replaces
 * budgeted ~6s for a fast turn and ~15s for a slow one — so the generation
 * escalation waits longer before speaking up, and talks about the writing
 * rather than the wake, which by then has already been paid.
 */
export const GENERATION_ESCALATION_SECONDS = 12;

/**
 * The honest escalation line, or null while the wait is still unremarkable.
 * Keyed on the milestone as well as the clock so it never blames a cold start
 * for time that was actually spent generating.
 *
 * `elapsedSeconds` is time spent in the **current** milestone, not total boot
 * time: the thresholds below are "this step has been running a while", and a
 * paid-for 16s wake must not immediately trip the generation line.
 */
export function bootEscalation(
  mode: SessionMode,
  milestone: BootMilestone,
  elapsedSeconds: number,
): string | null {
  // Nothing to escalate before a request is in flight, or once it has settled.
  if (milestone === "idle" || milestone === "ready" || milestone === "failed")
    return null;

  if (milestone === "requested") {
    if (elapsedSeconds < WAKE_ESCALATION_SECONDS) return null;
    return "The first session of the day takes a little longer while the tutor wakes up.";
  }

  if (elapsedSeconds < GENERATION_ESCALATION_SECONDS) return null;
  return mode === "mock"
    ? "Still writing. A fresh set of questions takes longer than a canned one."
    : "Still working. Writing something new takes longer than reusing an old question.";
}
